import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'next/navigation';
import { nanoid } from 'nanoid';
import { IDENTIFIERS, getUrl } from '@helpers/urlsHelper';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import * as format from '@format';
import { HookResponse } from './common';
import DashboardContext from '../../DashboardContext';

const getDetailPagePath: HookResponse['getDetailPagePath'] = ({ id }) => getUrl(IDENTIFIERS.DASHBOARD_BOOKINGS_ITEM, { id });

const useBookings = ({
  arePast,
  creatorId,
  memberId,
  page,
  perPage,
}: {
  arePast: boolean;
  creatorId: string | undefined;
  memberId: string | undefined;
  page: number;
  perPage: number;
}): HookResponse => {
  const { creator, isInitiateBookingModalOpen } = useContext(DashboardContext);

  const [isFetching, setIsFetching] = useState(false);
  const pathParams = useParams<{ id?: string }>();
  const [items, setItems] = useState<HookResponse['items']>([]);
  const [numItems, setNumItems] = useState<HookResponse['numItems']>();
  const mostRecentRequestRef = useRef<string | null>(null);

  const { getData: getBookingsForCreatorFn } = useNoodleApi(tsClient.getBookingsForCreator, { toastOnError: true });
  const { getData: getCreatorTeamFn } = useNoodleApi(tsClient.creators.getCreatorTeam);

  const selectedId = pathParams?.id;

  const loadPage = useCallback(async (): Promise<void> => {
    if (creatorId) {
      const thisRequestId = nanoid();
      mostRecentRequestRef.current = thisRequestId;

      setIsFetching(true);
      const response = await getBookingsForCreatorFn({
        arePast,
        creatorId,
        endDate: arePast
          ? new Date().toISOString()
          : new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString(),
        page,
        perPage,
        startDate: arePast
          ? new Date(0).toISOString()
          : new Date(Date.now() - 3600000).toISOString(),
        teamMemberPersonId: memberId === 'all' ? undefined : memberId,
      });

      const teamData = await getCreatorTeamFn({ creatorId, page: 1, perPage: 1000 });

      if (mostRecentRequestRef.current === thisRequestId) {
        if (response.data && teamData.data) {
          const bookings = arePast
            ? ( response.data.items || [])
            :( response.data.items || []).filter(booking => new Date(booking.booking.endAt || '') > new Date());
          const teamMembers = teamData.data.items;
          setNumItems(response.data.numItems);
          setItems(bookings.map(booking => ({
            bottomLine: booking.booking.teamMemberPersonId ? `with ${teamMembers.find(member => member.id === booking.booking.teamMemberPersonId)?.name}` : 'No one assigned',
            href: getUrl(IDENTIFIERS.DASHBOARD_BOOKINGS_ITEM, { id: booking.booking.id }),
            id: booking.booking.id,
            label: booking.booking.person.name || 'No name',
            topLine: booking.booking.startAt ? `${format.datetimeLocalTZ.humanReadable({ datetime: booking.booking.startAt, timezone: creator?.timezone })}` : '',
          })));
        }
        setIsFetching(false);
      }
    } else {
      setItems([]);
    }
  }, [
    arePast,
    creatorId,
    getBookingsForCreatorFn,
    getCreatorTeamFn,
    memberId,
    creator,
    page,
    perPage,
  ]);

  useEffect(() => {
    if (!isInitiateBookingModalOpen) {
      loadPage();
    }
  }, [isInitiateBookingModalOpen, loadPage]);

  return useMemo(() => ({
    getDetailPagePath,
    ifNoItemsText: 'No bookings yet',
    isFetchingList: isFetching,
    items,
    numItems,
    reloadList: () => loadPage(),
    selectedId,
  }), [
    isFetching,
    items,
    selectedId,
    loadPage,
    numItems,
  ]);
};

export default useBookings;
